import { useEffect, useRef } from "react";

// A template written as a full document (its own <head>/<style>) is sent back
// whole; a fragment (<p>/<table>/<br>…) is sent as just the body's HTML.
const FULL_DOC = /<(html|head|style)[\s>]/i;

// Shown only while editing a fragment; never part of what is sent.
const FRAGMENT_STYLE = 'body{margin:12px;font:400 13px/20px "Poppins",Arial,sans-serif;color:#1b1b1b}';

// Edits the bank email as HTML, so the template's formatting (tables, bold,
// links, inline styles) reaches the bank exactly as it is shown here. The
// template sits in a sandboxed iframe, so its scripts can't run and its
// styles can't leak into the app. The iframe's body is made editable, and
// every edit emits the updated HTML.
export default function MailBodyEditor({ html, onChange }) {
  const frameRef = useRef(null);
  // The last HTML this editor emitted. When it comes back as the html prop,
  // the iframe is left alone so the caret doesn't jump.
  const emittedRef = useRef(null);
  const fullDocRef = useRef(false);

  useEffect(() => {
    if (html === emittedRef.current || !frameRef.current) return;
    fullDocRef.current = FULL_DOC.test(html || "");
    frameRef.current.srcdoc = html || "";
  }, [html]);

  const onLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;
    if (!fullDocRef.current) {
      const style = doc.createElement("style");
      style.textContent = FRAGMENT_STYLE;
      doc.head.appendChild(style);
    }
    doc.body.contentEditable = "true";
    doc.body.addEventListener("input", () => {
      let out;
      if (fullDocRef.current) {
        const root = doc.documentElement.cloneNode(true);
        root.querySelector("body")?.removeAttribute("contenteditable");
        out = "<!DOCTYPE html>" + root.outerHTML;
      } else {
        out = doc.body.innerHTML;
      }
      emittedRef.current = out;
      onChange(out);
    });
  };

  return <iframe ref={frameRef} title="Email message" className="mail-box__editor" sandbox="allow-same-origin" onLoad={onLoad} />;
}
